import { authInfo } from '../../ducks/auth.duck';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { guestStart } from '../../util/api';
import { createSlug } from '../../util/urlHelpers';

// Error signal surfaced by api.guestStart (from the server's 409 body) when the email already has
// an account. The caller uses this to route the visitor to normal sign-in instead of retrying.
export const EMAIL_EXISTS_CODE = 'email-exists';

/**
 * Turn a guest's no-signup submission into a posted-job DRAFT.
 *
 * 1. POST /api/guest-start creates the Sharetribe account and logs the caller in — the server sets
 *    the session cookie on the response.
 * 2. authInfo() + fetchCurrentUser() sync Redux `state.auth.isAuthenticated` with that cookie.
 *    Without this the client still thinks it's logged out, so a push to the auth-gated post-a-job
 *    wizard would bounce to /signup (see src/ducks/auth.duck.js + Routes.js canShowComponent).
 * 3. The photos are uploaded and a DRAFT listing is created with them attached, plus the visitor's
 *    serviceType/junkType/loadSize, so the EXISTING wizard opens PREFILLED. A description is no
 *    longer collected anywhere, and
 *    the exact pickup location are intentionally left for the wizard (the location write is the
 *    security-sensitive sidecar path — let its own panel own it) — the user finishes + publishes there.
 *
 * The photos (File objects) are consumed HERE, before any navigation, so they never need to
 * survive a route change. On success, returns { id, slug } of the draft to navigate to. Any failure
 * rejects — api.guestStart's rejection carries `code: 'email-exists'` for the taken-email case.
 *
 * @param {Object} params
 * @param {string} params.email
 * @param {string} params.captchaToken   a passed Turnstile token
 * @param {Array}  params.photos         [{ id, file }] raw File objects from the form
 * @param {Object} params.listingData    { title, publicData } passed to ownListings.createDraft
 */
export const startGuestPost = ({ email, captchaToken, photos = [], listingData }) => async (
  dispatch,
  getState,
  sdk
) => {
  // 1. Create the account + session — but ONLY if the visitor isn't already authenticated. This
  //    makes the step idempotent on retry: if a FIRST attempt created the account + logged them in
  //    and then a later step failed (auth sync / upload / createDraft), calling guest-start again
  //    would 409 as 'email-exists' and strand a user who has no password yet. Skipping it lets the
  //    retry finish the draft. It also lets an already-signed-in visitor post from this page.
  if (!getState().auth.isAuthenticated) {
    // Throws (with `code: 'email-exists'` on a taken email) on 4xx.
    await guestStart({ email, captchaToken });

    // Sync Redux auth with the freshly-set session cookie before we navigate to the wizard.
    // enforce:true so the just-created user is fetched even if a stale currentUser is "fresh".
    await dispatch(authInfo());
    await dispatch(fetchCurrentUser({ afterLogin: true, enforce: true }));
  }

  // 3. Upload the photos, then create the draft with them attached + the collected fields.
  const uploads = await Promise.all(
    (photos || []).map(p => sdk.images.upload({ image: p.file }, { expand: true }))
  );
  const imageIds = uploads.map(r => r.data.data.id);

  const createResp = await sdk.ownListings.createDraft(
    { ...listingData, images: imageIds },
    { expand: true }
  );
  const listing = createResp.data.data;
  return { id: listing.id.uuid, slug: createSlug(listing.attributes.title) };
};
